/*
  (c) 2025 Fluxonix Inc.
*/
import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Grid, Typography, Divider, CircularProgress, Alert,
} from "@mui/material";
import { TrendingUp, Group, ErrorOutline } from "@mui/icons-material";
import { api } from "../api";
import RequireRole from "../components/RequireRole";

export default function AdminMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/api/admin/metrics");
        if (mounted) setMetrics(data);
      } catch (e) {
        if (mounted) setErr(e?.message || "Failed to load metrics");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Box p={4} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (err) {
    return (
      <Box p={4}>
        <Alert severity="error">{err}</Alert>
      </Box>
    );
  }

  return (
    <RequireRole role="admin">
    <Box p={4}>
      <Typography variant="h5" gutterBottom>📊 Platform Metrics</Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Group color="primary" />
                <Typography variant="subtitle2">Active Agents</Typography>
              </Box>
              <Typography variant="h5" mt={1}>
                {metrics?.active_agents ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="success" />
                <Typography variant="subtitle2">Total Sessions</Typography>
              </Box>
              <Typography variant="h5" mt={1}>
                {metrics?.total_sessions ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ErrorOutline color="error" />
                <Typography variant="subtitle2">Errors Logged</Typography>
              </Box>
              <Typography variant="h5" mt={1}>
                {metrics?.error_count ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
    </RequireRole>
  );
}
